import { forwardRef, useState, type KeyboardEvent } from "react";
import {
  CheckboxField,
  TextField,
  type TextFieldProps,
} from "@altek/ui/components/form/TextField";

/** Alanın altında, ipucunun önünde. Kısa: kişi yazarken okuyor. */
export const CAPS_LOCK_WARNING = "Büyük harf kilidi (Caps Lock) açık.";

/**
 * PAROLA ALANI — «Parolayı göster» ve Caps Lock uyarısıyla (9 Ekim 2026, 5. tur).
 *
 * Giriş, davet, sıfırlama ve yeniden doğrulama düz bir `type="password"` alanıydı:
 * - GÖSTER YOKTU. Yeni parola ekranlarında ikinci bir «tekrar yazın» alanı da yok, yani yeni
 *   parolada yapılan tek bir yazım hatası kişiyi hesabın dışında bırakıyordu (yeni bir sıfırlama
 *   bağlantısı gerekir). Yazdığını görmek, ikinci kez yazdırmaktan sade.
 * - CAPS LOCK SESSİZDİ. Girişte beş hatalı parola hesabı 15 dakika kilitliyor; Caps Lock açık
 *   unutulmuş bir klavye bu beş denemeyi kişi fark etmeden harcıyordu.
 *
 * NEDEN ONAY KUTUSU, göz düğmesi değil: kitin `TextField`'ının sonekine düğme konamıyor
 * (`pointer-events-none`), `SecretField`'ın gösterimi ise yazdıkça yeniden maskeleniyor ve bir
 * «panoya kopyala» düğmesi taşıyor — anahtar için doğru, yazılan parola için değil. Kitin kendi
 * `CheckboxField`'ı klavyeyle ve ekran okuyucuyla zaten doğru; göz düğmeli bir parola alanı kit
 * isteği olarak raporlandı.
 *
 * Caps Lock bilgisi yalnız TUŞ OLAYINDAN okunabiliyor (`getModifierState`); alan odağı kaybedince
 * uyarı kalkıyor, çünkü odak dışındayken durum bilinmiyor. Bilinmeyeni söylemektense susmak.
 * Alanın bir hatası varsa kit ipucunun yerine hatayı yazıyor — uyarı o anda görünmez.
 */
export const PasswordField = forwardRef<HTMLInputElement, Omit<TextFieldProps, "type">>(
  function PasswordField({ hint, onKeyDown, onKeyUp, onBlur, ...rest }, ref) {
    const [revealed, setRevealed] = useState(false);
    const [capsLock, setCapsLock] = useState(false);

    const readCapsLock = (event: KeyboardEvent<HTMLInputElement>) => {
      setCapsLock(
        typeof event.getModifierState === "function" && event.getModifierState("CapsLock"),
      );
    };

    return (
      <div>
        <TextField
          ref={ref}
          {...rest}
          type={revealed ? "text" : "password"}
          // Gösterilirken tarayıcının yazım düzeltmesi ve otomatik büyük harfi parolayı bozmasın.
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          hint={
            capsLock ? (
              <>
                <span className="font-medium text-warning-ink">{CAPS_LOCK_WARNING}</span>
                {hint ? <> {hint}</> : null}
              </>
            ) : (
              hint
            )
          }
          onKeyDown={(event) => {
            readCapsLock(event);
            onKeyDown?.(event);
          }}
          onKeyUp={(event) => {
            readCapsLock(event);
            onKeyUp?.(event);
          }}
          onBlur={(event) => {
            setCapsLock(false);
            onBlur?.(event);
          }}
        />
        <CheckboxField
          // Telefonda satır 44 px (ölçüldü: kitin satırı 21 px, yorumu «44px row on touch»
          // diyor — kit isteği). Satırın tamamı tıklanıyor; yalnız yüksekliği büyüyor.
          className="mt-2 max-md:[&_label]:min-h-11 max-md:[&_label]:items-center"
          label="Parolayı göster"
          checked={revealed}
          onChange={setRevealed}
          locked={rest.locked}
        />
      </div>
    );
  },
);
